// ============================================================
// src/pages/MyTimesheetPage.jsx
// All date inputs use custom DatePicker — mm-dd-yyyy format
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getMyWeek, addEntry, updateEntry, deleteEntry, submitWeek } from '../api/timesheetApi';
import { getDayLabel, formatTime } from '../utils/dateHelper';
import { Toast, StatCard, Btn, Field, Input, Modal, StatusBadge, Spinner, CardTitle, PageHeader } from '../components/UI';
import { useToast } from '../hooks/useToast';

// ════════════════════════════════════════════════════════════
// ── Date helpers (mm-dd-yyyy as the one true format) ────────
// ════════════════════════════════════════════════════════════

/** Date object → "mm-dd-yyyy" */
function toMMDDYYYY(d) {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}-${dd}-${d.getFullYear()}`;
}

/** Get today as "mm-dd-yyyy" (local, no UTC shift) */
function getTodayMDY() {
  return toMMDDYYYY(new Date());
}

/** Get Saturday of current week as "mm-dd-yyyy" */
function getCurrentWeekEnding() {
  const today = new Date();
  const sat = new Date(today);
  sat.setDate(today.getDate() + (6 - today.getDay()));
  return toMMDDYYYY(sat);
}

/**
 * Normalize ANY date format → "mm-dd-yyyy"
 * Handles: "yyyy-mm-dd", "mm-dd-yyyy", "2025-03-15T00:00:00Z", etc.
 */
function toMDY(raw) {
  if (!raw) return '';
  const s = String(raw).slice(0, 10); // grab just the date part
  // Already mm-dd-yyyy?
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;
  // yyyy-mm-dd → mm-dd-yyyy
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    return `${m}-${d}-${y}`;
  }
  return s;
}

/**
 * "mm-dd-yyyy" → "yyyy-mm-dd"
 * Used only when sending to API or comparing with ISO keys.
 */
function mdyToISO(mdy) {
  if (!mdy) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(mdy)) return mdy; // already ISO
  const [m, d, y] = mdy.split('-');
  return `${y}-${m}-${d}`;
}

/** "mm-dd-yyyy" → JS Date or null */
function mdyToDate(mdy) {
  if (!mdy || !/^\d{2}-\d{2}-\d{4}$/.test(mdy)) return null;
  const [m, d, y] = mdy.split('-');
  const dt = new Date(+y, +m - 1, +d);
  return isNaN(dt.getTime()) ? null : dt;
}

// ════════════════════════════════════════════════════════════
// ── Custom DatePicker (zero deps, position:fixed popup) ─────
// ════════════════════════════════════════════════════════════
const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function DatePicker({ value, onChange, small = false }) {
  const selected    = mdyToDate(value);
  const today       = new Date();
  const [open, setOpen]           = useState(false);
  const [viewYear,  setViewYear]  = useState((selected || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((selected || today).getMonth());
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  useEffect(() => {
    const d = mdyToDate(value);
    if (d) { setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); }
  }, [value]);

  const prevMonth = () => viewMonth === 0  ? (setViewMonth(11), setViewYear(y => y - 1)) : setViewMonth(m => m - 1);
  const nextMonth = () => viewMonth === 11 ? (setViewMonth(0),  setViewYear(y => y + 1)) : setViewMonth(m => m + 1);

  function buildDays() {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    return cells;
  }

  const isSelected = d => d && selected &&
    selected.getFullYear() === viewYear && selected.getMonth() === viewMonth && selected.getDate() === d;
  const isToday    = d => d &&
    today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === d;

  function selectDay(day) {
    if (!day) return;
    onChange(toMMDDYYYY(new Date(viewYear, viewMonth, day)));
    setOpen(false);
  }

  const years = [];
  for (let y = today.getFullYear() - 5; y <= today.getFullYear() + 5; y++) years.push(y);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      {/* Trigger */}
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          type="text" readOnly
          value={value || ''}
          placeholder="mm-dd-yyyy"
          onClick={() => setOpen(o => !o)}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: `1.5px solid ${open ? '#dc2626' : '#d1d5db'}`,
            borderRadius: '6px',
            padding: small ? '0.28rem 2.2rem 0.28rem 0.55rem' : '0.48rem 2.4rem 0.48rem 0.75rem',
            fontSize: small ? '0.8rem' : '0.875rem',
            color: value ? '#111827' : '#9ca3af',
            background: '#fff', outline: 'none', cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
        />
        <span onClick={() => setOpen(o => !o)} style={{
          position: 'absolute', right: '0.55rem', top: '50%',
          transform: 'translateY(-50%)',
          fontSize: small ? '0.9rem' : '1rem',
          cursor: 'pointer', userSelect: 'none', lineHeight: 1,
        }}>📅</span>
      </div>

      {/* Popup — fixed, auto-flips */}
      {open && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0,
            zIndex: 99999, background: '#fff',
            border: '1px solid #e5e7eb', borderRadius: '12px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            width: '288px', userSelect: 'none',
          }}
          ref={el => {
            if (!el || !wrapRef.current) return;
            const rect  = wrapRef.current.getBoundingClientRect();
            const popH  = el.offsetHeight || 300;
            const below = window.innerHeight - rect.bottom;
            el.style.top = below >= popH + 8 || below >= rect.top
              ? `${rect.bottom + 6}px`
              : `${rect.top - popH - 6}px`;
            let left = rect.left;
            if (left + 288 > window.innerWidth - 8) left = window.innerWidth - 296;
            if (left < 8) left = 8;
            el.style.left = `${left}px`;
          }}
        >
          {/* Header */}
          <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <button onClick={prevMonth} style={navBtn}>‹</button>
              <div style={{ display: 'flex', gap: '6px' }}>
                <select value={viewMonth} onChange={e => setViewMonth(+e.target.value)} style={dropStyle}>
                  {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
                <select value={viewYear} onChange={e => setViewYear(+e.target.value)} style={dropStyle}>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button onClick={nextMonth} style={navBtn}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
              {DAYS.map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: '0.62rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', lineHeight: '26px' }}>{d}</div>
              ))}
            </div>
          </div>

          {/* Days grid */}
          <div style={{ padding: '8px 10px 12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
              {buildDays().map((day, i) => (
                <div key={i} onClick={() => selectDay(day)} style={{
                  height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '7px', fontSize: '0.82rem',
                  cursor: day ? 'pointer' : 'default',
                  fontWeight: isSelected(day) || isToday(day) ? 700 : 400,
                  background: isSelected(day) ? '#dc2626' : 'transparent',
                  color: isSelected(day) ? '#fff' : isToday(day) ? '#dc2626' : day ? '#374151' : 'transparent',
                  transition: 'background 0.1s, color 0.1s',
                }}
                  onMouseEnter={e => { if (day && !isSelected(day)) { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; } }}
                  onMouseLeave={e => { if (day && !isSelected(day)) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isToday(day) ? '#dc2626' : '#374151'; } }}
                >{day || ''}</div>
              ))}
            </div>
            {/* Today shortcut */}
            <div style={{ textAlign: 'center', marginTop: '8px', borderTop: '1px solid #f3f4f6', paddingTop: '8px' }}>
              <button onClick={() => { onChange(getTodayMDY()); setOpen(false); }} style={{
                background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px',
                padding: '3px 14px', fontSize: '0.75rem', fontWeight: 600,
                color: '#374151', cursor: 'pointer',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#dc2626'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
              >Today</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn   = { background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', color: '#6b7280', lineHeight: 1 };
const dropStyle = { fontSize: '0.78rem', fontWeight: 600, border: '1px solid #e5e7eb', borderRadius: '5px', padding: '3px 6px', color: '#374151', background: '#f9fafb', outline: 'none', cursor: 'pointer' };

// ════════════════════════════════════════════════════════════
// ── Page ────────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════
export default function MyTimesheetPage() {
  const { toast, showToast, clearToast } = useToast();

  // All date state in "mm-dd-yyyy"
  const [weekEnding,   setWeekEnding]   = useState(getCurrentWeekEnding());
  const [week,         setWeek]         = useState(null);
  const [entries,      setEntries]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [submitOpen,   setSubmitOpen]   = useState(false);
  const [submitting,   setSubmitting]   = useState(false);

  const [viewEntry,    setViewEntry]    = useState(null);
  const [editEntry,    setEditEntry]    = useState(null);
  const [editForm,     setEditForm]     = useState(null);
  const updEdit = k => e => setEditForm(f => ({ ...f, [k]: e.target.value }));

  // ── Add form — all dates in mm-dd-yyyy ──
  const initForm = () => ({
    entry_date:   getTodayMDY(),   // mm-dd-yyyy
    work_order:   '',
    job_number:   '',
    description:  '',
    notes:        '',
    time_in:      '07:00',
    time_out:     '15:30',
    hours_worked: '',
  });
  const [form, setForm] = useState(initForm);
  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // ── Auto-calc hours — Add form ──
  useEffect(() => {
    const [h1, m1] = form.time_in.split(':').map(Number);
    const [h2, m2] = form.time_out.split(':').map(Number);
    if (isNaN(h1) || isNaN(h2)) return;
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins < 0) mins += 1440;
    const hrs = Math.round((mins / 60) * 2) / 2;
    setForm(f => ({ ...f, hours_worked: hrs > 0 ? String(hrs) : '' }));
  }, [form.time_in, form.time_out]);

  // ── Auto-calc hours — Edit form ──
  useEffect(() => {
    if (!editForm) return;
    const [h1, m1] = editForm.time_in.split(':').map(Number);
    const [h2, m2] = editForm.time_out.split(':').map(Number);
    if (isNaN(h1) || isNaN(h2)) return;
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins < 0) mins += 1440;
    const hrs = Math.round((mins / 60) * 2) / 2;
    setEditForm(f => ({ ...f, hours_worked: hrs > 0 ? String(hrs) : '' }));
  }, [editForm?.time_in, editForm?.time_out]);

  // ── Fetch week ──
  const fetchWeek = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyWeek(weekEnding);  // passes mm-dd-yyyy
      setWeek(res.week);
      setEntries(res.entries || []);
    } catch (err) {
      showToast(err.message || 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  }, [weekEnding, showToast]);

  useEffect(() => { fetchWeek(); }, [fetchWeek]);

  // ── Add entry ──
  async function handleAdd(e) {
    e.preventDefault();
    if (!form.entry_date || !form.job_number.trim() || !form.description.trim()) {
      showToast('Date, Job # and Description are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await addEntry({
        entry_date:   form.entry_date,    // already mm-dd-yyyy
        work_order:   form.work_order.trim(),
        job_number:   form.job_number.trim(),
        description:  form.description.trim(),
        notes:        form.notes.trim(),
        time_in:      form.time_in,
        time_out:     form.time_out,
        hours_worked: form.hours_worked,
      });
      showToast('Entry added ✓', 'success');
      setForm(f => ({ ...f, work_order: '', job_number: '', description: '', notes: '' }));
      fetchWeek();
    } catch (err) {
      showToast(err.message || 'Failed to add entry', 'error');
    } finally {
      setSaving(false);
    }
  }

  // ── Edit entry save ──
  async function handleEditSave() {
    if (!editForm.job_number.trim() || !editForm.description.trim()) {
      showToast('Job # and Description are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await updateEntry(editEntry.id, {
        entry_date:   editForm.entry_date,   // mm-dd-yyyy
        work_order:   editForm.work_order.trim(),
        job_number:   editForm.job_number.trim(),
        description:  editForm.description.trim(),
        notes:        editForm.notes.trim(),
        time_in:      editForm.time_in,
        time_out:     editForm.time_out,
        hours_worked: editForm.hours_worked,
      });
      showToast('Entry updated ✓', 'success');
      setEditEntry(null); setEditForm(null);
      fetchWeek();
    } catch (err) {
      showToast(err.message || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  }

  // ── Delete ──
  async function handleDelete(id) {
    if (!window.confirm('Remove this entry?')) return;
    try {
      await deleteEntry(id);
      showToast('Entry removed', 'warning');
      fetchWeek();
    } catch (err) {
      showToast(err.message || 'Delete failed', 'error');
    }
  }

  // ── Submit week ──
  async function handleSubmit() {
    if (!week?.id) return;
    setSubmitting(true);
    try {
      await submitWeek(week.id);
      showToast('Timesheet submitted for approval ✅', 'success');
      setSubmitOpen(false);
      fetchWeek();
    } catch (err) {
      showToast(err.message || 'Submit failed', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  // ── 7-day grid (Mon → Sun) ──
  const weekGrid = () => {
    const iso = mdyToISO(week?.week_ending_date || weekEnding);
    const end = new Date(`${iso}T00:00:00`);
    const localToday = getTodayMDY();

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(end);
      d.setDate(end.getDate() - (6 - i));
      // Build ISO key locally (no UTC shift)
      const isoKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const mdyKey = toMMDDYYYY(d);

      const rows = entries.filter(en => mdyToISO(toMDY(en.entry_date)) === isoKey);
      const hrs  = rows.reduce((s, en) => s + parseFloat(en.hours_worked || 0), 0);
      return {
        day:   ['SUN','MON','TUE','WED','THU','FRI','SAT'][d.getDay()],
        date:  `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
        hrs, jobs: rows.length,
        today: localToday === mdyKey,
      };
    });
  };

  const totalHrs = entries.reduce((s, e) => s + parseFloat(e.hours_worked || 0), 0);
  const regHrs   = Math.min(totalHrs, 40);
  const otHrs    = Math.max(totalHrs - 40, 0);
  const canEdit  = !week || ['draft', 'rejected'].includes(week?.status);

  if (loading) return <Spinner text="Loading timesheet…" />;

  return (
    <div>
      <PageHeader
        title="MY TIMESHEET"
        subtitle={`Week Ending: ${weekEnding}`}
        actions={canEdit && entries.length > 0
          ? <Btn onClick={() => setSubmitOpen(true)}>✅ Submit for Approval</Btn>
          : null}
      />

      {/* ── Week selector — DatePicker ── */}
      <div className="filter-row mb-4">
        <div className="filter-group">
          <span className="filter-label">Week Ending</span>
          {/* ✅ Custom DatePicker — always mm-dd-yyyy, no locale issues */}
          <div style={{ width: '180px' }}>
            <DatePicker
              value={weekEnding}
              onChange={setWeekEnding}
            />
          </div>
        </div>
        {week && <StatusBadge status={week.status} />}
        {week?.status === 'rejected' && week.reviewer_notes && (
          <span className="alert alert-warn" style={{ padding: '0.25rem 0.625rem', fontSize: '0.75rem' }}>
            Rejected: {week.reviewer_notes}
          </span>
        )}
      </div>

      {/* ── 7-day grid ── */}
      <div className="week-grid mb-4">
        {weekGrid().map((d, i) => (
          <div key={i} className={`day-cell${d.today ? ' today' : ''}`}>
            {d.today && <span className="day-cell-today-tag">TODAY</span>}
            <div className="day-cell-day">{d.day}</div>
            <div className="day-cell-date">{d.date}</div>
            <div className={`day-cell-hrs${d.hrs > 0 ? ' has-hrs' : ' no-hrs'}`}>
              {d.hrs > 0 ? d.hrs : '—'}
            </div>
            <div className="day-cell-jobs">
              {d.jobs > 0 ? `${d.jobs} job${d.jobs > 1 ? 's' : ''}` : 'no entry'}
            </div>
          </div>
        ))}
      </div>

      {/* ── Add entry form ── */}
      {canEdit && (
        <div className="card mb-4">
          <CardTitle>➕ Add Daily Entry</CardTitle>
          <div className="card-body">
            <form onSubmit={handleAdd}>
              <div className="form-grid mb-3"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(9rem, 1fr))' }}>

                {/* ✅ Date — custom DatePicker */}
                <Field label="Date" required>
                  <DatePicker
                    value={form.entry_date}
                    onChange={val => setForm(f => ({ ...f, entry_date: val }))}
                  />
                </Field>

                <Field label="WO # (Work Order)">
                  <Input type="text" value={form.work_order} onChange={upd('work_order')} placeholder="e.g. WO-1021" />
                </Field>
                <Field label="Unit / Job Number" required>
                  <Input type="text" value={form.job_number} onChange={upd('job_number')} placeholder="e.g. JOB-2841" required />
                </Field>
                <Field label="Time In" required>
                  <Input type="time" value={form.time_in} onChange={upd('time_in')} required />
                </Field>
                <Field label="Time Out" required>
                  <Input type="time" value={form.time_out} onChange={upd('time_out')} required />
                </Field>
                <Field label={`Hours Worked${form.hours_worked ? ` (${form.hours_worked} HRS)` : ''}`}>
                  <Input type="number" value={form.hours_worked} readOnly placeholder="Auto" step="0.5" />
                </Field>
                <Field label="Work Description" required>
                  <Input type="text" value={form.description} onChange={upd('description')}
                    placeholder="Work description…" required />
                </Field>
                <Field label="Notes">
                  <Input type="text" value={form.notes} onChange={upd('notes')}
                    placeholder="Additional notes…" />
                </Field>
                <Field label=" ">
                  <Btn type="submit" disabled={saving} style={{ width: '100%' }}>
                    {saving ? 'SAVING…' : 'ADD ENTRY'}
                  </Btn>
                </Field>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Entries table ── */}
      <div className="card">
        <div className="card-header">
          <span>This Week's Entries</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
            Total: <strong style={{ color: '#cc1111' }}>{totalHrs.toFixed(1)}</strong> hrs
          </span>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                {['Date','Day','WO #','Unit / Job #','Description','Time In','Time Out','Hours','Status','Action'].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan={10} className="tbl-empty">No entries yet. Add your first entry above.</td></tr>
              ) : entries.map(en => (
                <tr key={en.id}>
                  {/* ✅ Normalize date from API → mm-dd-yyyy display */}
                  <td className="td-mono">{toMDY(en.entry_date)}</td>
                  <td className="td-muted" style={{ fontWeight: 700, fontSize: '0.65rem' }}>
                    {getDayLabel(en.entry_date)}
                  </td>
                  <td className="td-mono" style={{ color: '#7c3aed' }}>{en.work_order || '—'}</td>
                  <td className="td-mono td-red">{en.job_number}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{en.description}</div>
                    {en.notes && (
                      <div style={{ fontSize: '0.72rem', color: '#6b7280', fontStyle: 'italic', marginTop: '0.15rem' }}>
                        🔧 {en.notes}
                      </div>
                    )}
                  </td>
                  <td><span className="time-chip in">{formatTime(en.time_in)}</span></td>
                  <td><span className="time-chip out">{formatTime(en.time_out)}</span></td>
                  <td>
                    <span className={`hrs-val${parseFloat(en.hours_worked) > 8 ? ' over8' : ''}`}>
                      {parseFloat(en.hours_worked).toFixed(1)}
                      <span style={{ fontSize: '0.6rem', opacity: 0.5, marginLeft: '0.125rem' }}>h</span>
                    </span>
                  </td>
                  <td><StatusBadge status={week?.status || 'draft'} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'nowrap' }}>
                      <button className="btn btn-xs"
                        style={{ background: '#1f2937', color: '#fff', border: 'none', whiteSpace: 'nowrap' }}
                        onClick={() => setViewEntry(en)}>
                        👁 VIEW
                      </button>
                      {canEdit && (
                        <button className="btn btn-xs"
                          style={{ background: '#f59e0b', color: '#fff', border: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => {
                            setEditEntry(en);
                            setEditForm({
                              entry_date:   toMDY(en.entry_date),  // ✅ always mm-dd-yyyy
                              work_order:   en.work_order   || '',
                              job_number:   en.job_number   || '',
                              description:  en.description  || '',
                              notes:        en.notes        || '',
                              time_in:      en.time_in?.slice(0, 5)  || '07:00',
                              time_out:     en.time_out?.slice(0, 5) || '15:30',
                              hours_worked: en.hours_worked || '',
                            });
                          }}>
                          ✏️ EDIT
                        </button>
                      )}
                      {canEdit && (
                        <button className="btn btn-danger btn-xs" onClick={() => handleDelete(en.id)}>
                          DEL
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Submit modal ── */}
      <Modal open={submitOpen} title="Submit Timesheet for Approval"
        onClose={() => setSubmitOpen(false)}
        footer={<>
          <Btn variant="secondary" onClick={() => setSubmitOpen(false)}>CANCEL</Btn>
          <Btn variant="success" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'SUBMITTING…' : '✅ CONFIRM SUBMIT'}
          </Btn>
        </>}>
        <p className="text-sm" style={{ color: '#6b7280', marginBottom: '1rem' }}>
          Submit week ending <strong style={{ color: '#cc1111' }}>{weekEnding}</strong> for supervisor approval.
          You cannot edit entries after submission.
        </p>
        <div className="info-block">
          {[
            ['Total Hours',    `${totalHrs.toFixed(1)} hrs`, totalHrs > 0],
            ['Regular (≤40)',  `${regHrs.toFixed(1)} hrs`,   false],
            ['Overtime (>40)', `${otHrs.toFixed(1)} hrs`,    otHrs > 0],
          ].map(([l, v, red]) => (
            <div key={l} className="info-row">
              <span>{l}</span>
              <span style={red ? { color: '#cc1111' } : {}}>{v}</span>
            </div>
          ))}
        </div>
      </Modal>

      {/* ── View Entry modal ── */}
      <Modal open={!!viewEntry} title="Entry Details"
        onClose={() => setViewEntry(null)}
        footer={<Btn variant="secondary" onClick={() => setViewEntry(null)}>CLOSE</Btn>}>
        {viewEntry && (
          <div className="info-block">
            {[
              ['Date',         toMDY(viewEntry.entry_date)],   // ✅ mm-dd-yyyy
              ['Day',          getDayLabel(viewEntry.entry_date)],
              ['WO #',         viewEntry.work_order || '—'],
              ['Unit / Job #', viewEntry.job_number],
              ['Time In',      formatTime(viewEntry.time_in)],
              ['Time Out',     formatTime(viewEntry.time_out)],
              ['Hours',        `${parseFloat(viewEntry.hours_worked).toFixed(1)} hrs`],
              ['Description',  viewEntry.description],
              ['Notes',        viewEntry.notes || '—'],
              ['Status',       week?.status || 'draft'],
            ].map(([l, v]) => (
              <div key={l} className="info-row">
                <span style={{ color: '#6b7280' }}>{l}</span>
                <span style={{ fontWeight: 600 }}>{v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* ── Edit Entry modal ── */}
      <Modal open={!!editEntry} title="Edit Entry"
        onClose={() => { setEditEntry(null); setEditForm(null); }}
        footer={<>
          <Btn variant="secondary" onClick={() => { setEditEntry(null); setEditForm(null); }}>CANCEL</Btn>
          <Btn variant="success" onClick={handleEditSave} disabled={saving}>
            {saving ? 'SAVING…' : '💾 SAVE CHANGES'}
          </Btn>
        </>}>
        {editForm && (
          <div className="form-grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(9rem, 1fr))', gap: '0.75rem' }}>

            {/* ✅ Date — custom DatePicker in edit modal */}
            <Field label="Date" required>
              <DatePicker
                value={editForm.entry_date}
                onChange={val => setEditForm(f => ({ ...f, entry_date: val }))}
              />
            </Field>

            <Field label="WO # (Work Order)">
              <Input type="text" value={editForm.work_order} onChange={updEdit('work_order')} placeholder="e.g. WO-1021" />
            </Field>
            <Field label="Unit / Job Number" required>
              <Input type="text" value={editForm.job_number} onChange={updEdit('job_number')} placeholder="e.g. JOB-2841" required />
            </Field>
            <Field label="Time In">
              <Input type="time" value={editForm.time_in} onChange={updEdit('time_in')} />
            </Field>
            <Field label="Time Out">
              <Input type="time" value={editForm.time_out} onChange={updEdit('time_out')} />
            </Field>
            <Field label={`Hours Worked${editForm.hours_worked ? ` (${editForm.hours_worked})` : ''}`}>
              <Input type="number" value={editForm.hours_worked} readOnly placeholder="Auto" step="0.5" />
            </Field>
            <Field label="Work Description" required>
              <Input type="text" value={editForm.description} onChange={updEdit('description')}
                placeholder="Work description…" required />
            </Field>
            <Field label="Notes">
              <Input type="text" value={editForm.notes} onChange={updEdit('notes')}
                placeholder="Additional notes…" />
            </Field>
          </div>
        )}
      </Modal>

      <Toast message={toast.message} type={toast.type} onClose={clearToast} />
    </div>
  );
}